// Every NeverBounce request the tool has made — who, what, which button, what it cost.
// This screen exists because on 8-9 September 2026 the answer to all four questions
// had to be reconstructed from a CSV export out of NeverBounce.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, VerifyLogFilter, VerifyLogRow } from "../lib/api";

export default function VerifyLog() {
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState<VerifyLogRow[]>([]);
  const [byUser, setByUser] = useState<{ day: string; user_email: string | null; calls: number; credits: number }[]>([]);
  const [totals, setTotals] = useState({ total: 0, credits: 0, usd: 0, distinct: 0, people: 0, pages: 1, creditUsd: 0.008 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const filter: VerifyLogFilter = useMemo(() => ({
    q:      params.get("q") || "",
    user:   params.get("user") || "",
    source: params.get("source") || "",
    result: params.get("result") || "",
    billed: params.get("billed") === "1",
    from:   params.get("from") || "",
    to:     params.get("to") || "",
    page:   Number(params.get("page") || 1),
    per:    Number(params.get("per") || 100),
  }), [params]);

  const reload = useCallback(() => {
    setLoading(true); setError(null);
    api.verifyLog(filter)
      .then(r => {
        setRows(r.rows);
        setByUser(r.by_user);
        setTotals({
          total: r.total, credits: r.credits, usd: r.usd,
          distinct: r.distinct_addresses, people: r.people,
          pages: r.pages, creditUsd: r.credit_usd,
        });
      })
      .catch(e => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [filter]);
  useEffect(() => { reload(); }, [reload]);

  function setF(k: string, v: string | boolean) {
    const next = new URLSearchParams(params);
    if (v === "" || v === false) next.delete(k);
    else next.set(k, v === true ? "1" : String(v));
    if (k !== "page") next.delete("page");
    setParams(next);
  }
  function clearFilters() { setParams(new URLSearchParams()); }

  // Search box is debounced so typing doesn't fire a request per keystroke.
  const [qInput, setQInput] = useState(filter.q || "");
  useEffect(() => { setQInput(filter.q || ""); }, [filter.q]);
  const debounce = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => { if (qInput !== (filter.q || "")) setF("q", qInput); }, 350);
    return () => { if (debounce.current) clearTimeout(debounce.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qInput]);

  const wasted = rows.filter(r => r.credits > 0 && (r.result === "unknown" || r.result === "error")).length;

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tightish text-slate-900">Verification log</h1>
          <p className="mt-1 text-sm text-slate-500">
            {loading ? "Loading…" : `${totals.total.toLocaleString()} NeverBounce call(s) matching this filter`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/outreach/contacts" className="btn-secondary !text-xs !py-1 !px-2.5">← Contacts</Link>
          <Link to="/outreach/logs" className="btn-secondary !text-xs !py-1 !px-2.5">Email log</Link>
        </div>
      </div>

      {/* What this filter cost. */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <Stat label="Credits spent" value={totals.credits.toLocaleString()} tone={totals.credits > 0 ? "amber" : "slate"} />
        <Stat label="Cost" value={`USD $${totals.usd.toFixed(2)}`} tone={totals.usd > 0 ? "amber" : "slate"} />
        <Stat label="Calls made" value={totals.total.toLocaleString()} />
        <Stat label="Distinct addresses" value={totals.distinct.toLocaleString()} />
        <Stat label="People involved" value={String(totals.people)} />
      </div>

      {totals.total > totals.distinct && (
        <div className="rounded-md bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
          <strong>{(totals.total - totals.distinct).toLocaleString()}</strong> call(s) were repeats of an address already
          checked in this filter — {((totals.total - totals.distinct) * totals.creditUsd).toFixed(2)} USD.
        </div>
      )}

      {/* Spend per person per day. */}
      {byUser.length > 0 && (
        <div className="card p-4">
          <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Spend by person, by day</div>
          <div className="overflow-x-auto">
            <table className="refined [&_tbody_td]:!px-3 [&_tbody_td]:!py-1.5 [&_tbody_td]:!text-[11px] [&_thead_th]:!px-3 [&_thead_th]:!py-1.5">
              <thead><tr><th>Day</th><th>Who</th><th className="text-right">Calls</th><th className="text-right">Credits</th><th className="text-right">USD</th></tr></thead>
              <tbody>
                {byUser.map((b, i) => (
                  <tr key={i}>
                    <td className="whitespace-nowrap text-slate-700">{b.day}</td>
                    <td className="text-slate-800">
                      <button className="text-indigo-700 hover:underline" onClick={() => setF("user", b.user_email || "")}>
                        {b.user_email || "—"}
                      </button>
                    </td>
                    <td className="text-right text-slate-600">{b.calls.toLocaleString()}</td>
                    <td className="text-right font-medium text-slate-800">{b.credits.toLocaleString()}</td>
                    <td className="text-right text-slate-700">${(b.credits * totals.creditUsd).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="card p-4">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
          <Field label="Search">
            <input type="text" className="w-full !h-8 !text-xs !py-1.5 !px-2.5" value={qInput}
                   placeholder="email / person / company…" onChange={(e) => setQInput(e.target.value)} />
          </Field>
          <Field label="From">
            <input type="date" className="w-full !h-8 !text-xs !py-1.5 !px-2.5" value={filter.from || ""} onChange={(e) => setF("from", e.target.value)} />
          </Field>
          <Field label="To">
            <input type="date" className="w-full !h-8 !text-xs !py-1.5 !px-2.5" value={filter.to || ""} onChange={(e) => setF("to", e.target.value)} />
          </Field>
          <Field label="Button">
            <select className="w-full !h-8 !text-xs !py-1 !px-2" value={filter.source || ""} onChange={(e) => setF("source", e.target.value)}>
              <option value="">All</option>
              <option value="modal">Test (modal)</option>
              <option value="bulk">Verify emails (bulk)</option>
              <option value="candidates">Candidates</option>
            </select>
          </Field>
          <Field label="Result">
            <select className="w-full !h-8 !text-xs !py-1 !px-2" value={filter.result || ""} onChange={(e) => setF("result", e.target.value)}>
              <option value="">All</option>
              {["valid","invalid","catchall","disposable","unknown","error"].map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </Field>
          <Field>
            <label className="flex h-8 items-center gap-1.5 text-xs text-slate-600">
              <input type="checkbox" checked={filter.billed || false} onChange={(e) => setF("billed", e.target.checked)} />
              Billed only
            </label>
          </Field>
        </div>
        <div className="mt-3 flex items-center gap-2 text-xs">
          <button className="btn-ghost !text-xs !py-1 !px-2" onClick={clearFilters}>Clear filters</button>
          {wasted > 0 && (
            <span className="text-[11px] text-slate-500">
              On this page, <strong className="text-amber-700">{wasted}</strong> billed call(s) returned no usable answer.
            </span>
          )}
        </div>
      </div>

      {error && <div className="card p-4 text-rose-700">{error}</div>}

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="refined [&_tbody_td]:!px-3 [&_tbody_td]:!py-2 [&_tbody_td]:!text-[11px] [&_thead_th]:!px-3 [&_thead_th]:!py-2">
            <thead>
              <tr>
                <th className="w-40">When</th>
                <th className="w-48">Who</th>
                <th>Address</th>
                <th>Contact</th>
                <th className="w-24">Button</th>
                <th className="w-24">Result</th>
                <th className="w-20 text-right">Cost</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id}>
                  <td className="whitespace-nowrap text-slate-600">{r.created_at}</td>
                  <td className="truncate text-slate-700" title={r.user_email || ""}>{r.user_email || "—"}</td>
                  <td className="font-mono text-[11px] text-slate-800">{r.email}</td>
                  <td className="max-w-[16rem] truncate text-slate-600" title={`${r.contact_name || ""} ${r.contact_company || ""}`}>
                    {r.contact_id
                      ? <Link to={`/outreach/contacts?q=${encodeURIComponent(r.contact_name || "")}`} className="text-indigo-700 hover:underline">
                          {r.contact_name || `#${r.contact_id}`}
                        </Link>
                      : <span className="text-slate-400">—</span>}
                    {r.contact_company && <span className="text-slate-400"> · {r.contact_company}</span>}
                  </td>
                  <td><SourcePill s={r.source} /></td>
                  <td>{r.result ? <ResultPill r={r.result} /> : <span className="text-slate-400">skipped</span>}</td>
                  <td className="whitespace-nowrap text-right">
                    {r.credits > 0
                      ? <span className="font-medium text-slate-800">${(r.credits * totals.creditUsd).toFixed(3)}</span>
                      : <span className="text-emerald-700" title={r.error || "not billed"}>free</span>}
                  </td>
                </tr>
              ))}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500">
                  No verification calls recorded for this filter. The log starts from 9 September 2026 —
                  earlier spend was never recorded.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {totals.pages > 1 && (
        <div className="flex items-center justify-center gap-1 text-xs">
          <button disabled={(filter.page || 1) <= 1} className="btn-secondary !text-xs !py-1 !px-2" onClick={() => setF("page", String((filter.page || 1) - 1))}>‹ Prev</button>
          <span className="px-2 text-slate-500">page {filter.page || 1} of {totals.pages}</span>
          <button disabled={(filter.page || 1) >= totals.pages} className="btn-secondary !text-xs !py-1 !px-2" onClick={() => setF("page", String((filter.page || 1) + 1))}>Next ›</button>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: "amber" | "slate" }) {
  return (
    <div className="card p-3">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-0.5 text-lg font-semibold ${tone === "amber" ? "text-amber-700" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}
function Field({ label, children }: { label?: string; children: React.ReactNode }) {
  return (
    <div>
      {/* Empty label keeps the control aligned with its labelled neighbours in the grid. */}
      <div className="mb-1 h-[13px] text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      {children}
    </div>
  );
}
function SourcePill({ s }: { s: string }) {
  const map: Record<string, string> = {
    modal: "pill pill-blue", bulk: "pill pill-amber", candidates: "pill pill-slate",
  };
  const label: Record<string, string> = { modal: "Test", bulk: "Bulk", candidates: "Cand." };
  return <span className={map[s] || "pill pill-slate"}>{label[s] || s}</span>;
}
function ResultPill({ r }: { r: string }) {
  const cls: Record<string, string> = {
    valid: "pill pill-green", invalid: "pill pill-rose", catchall: "pill pill-amber",
    disposable: "pill pill-rose", unknown: "pill pill-slate", error: "pill pill-rose",
  };
  return <span className={cls[r] || "pill pill-slate"}>{r}</span>;
}
